import { useState, useRef, useEffect } from 'react'
import useStyles from './styles'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import useTranslations from '../../../i18n/useTranslations'
import { ReactComponent as IconUpload } from '../../../assets/icons/upload.svg'
import { ReactComponent as IconClose } from '../../../assets/icons/trash.svg'
import { ReactComponent as IconSuccesful } from '../../../assets/icons/FileTextSuccess.svg'
import { ReactComponent as IconError } from '../../../assets/icons/warning.svg'
import { Plus } from 'phosphor-react'
import { generateRandomKey } from '../../../utils'
import { CUSTOM_FILE_TYPE } from '../../../common/constants'
import { useUserContext } from '../../../auth/UserProvider'
import { useUploadFiles } from '../../../services/user'
import { useQueryClient } from 'react-query'
import { MD5 } from 'crypto-js'

const FileUploader = ({
  centreCode,
  height,
  maxFiles,
  acceptFiles,
  initialFiles,
  onAdd,
  onDelete,
  onChange,
  onError,
}) => {
  const [files, setFiles] = useState(initialFiles ?? [])
  const [showAlert, setShowAlert] = useState()
  const inputRef = useRef()
  const t = useTranslations()
  const classes = useStyles()
  const queryClient = useQueryClient()

  const { clientId, centres } = useUserContext()
  const centreCodeId = centres.find(({ id }) => id === centreCode)?.hideId

  useEffect(() => {
    let timer
    if (showAlert !== null) {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 7500)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  const {
    data,
    mutate: uploadFile,
    isLoading: isLoadingFile,
    isError: isErrorFile,
  } = useUploadFiles(clientId, centreCodeId)

  const handleCancelAll = () => {
    queryClient.cancelMutations()
  }

  const handleDragOver = event => {
    event.preventDefault()
  }

  const handleCheckFiles = data => {
    const filesToCheck = data
    if (!filesToCheck?.length) {
      return
    }
    let filesToAdd = []
    const readFileAsync = async file => {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = function () {
          const base64String = reader.result.split(',')[1]
          const md5Hash = MD5(base64String).toString()
          resolve({
            id: generateRandomKey(),
            file: file,
            type: null,
            fileBodyPOST: {
              file_group: 'export-info',
              file_type: null,
              file_name: file.name,
              file_extension: file.name.split('.').pop(),
              file_size: file.size,
              compression_type: 'None',
              md5: md5Hash,
              data: base64String,
            },
          })
        }
        reader.readAsDataURL(file)
      })
    }

    const handleFilesAsync = async () => {
      for (let i = 0; i < filesToCheck?.length; i++) {
        if (!acceptFiles.includes(filesToCheck[i].name.split('.').pop())) return
        const fileToAdd = await readFileAsync(filesToCheck[i])
        filesToAdd.push(fileToAdd)
      }
      if (onAdd) onAdd(filesToAdd)
      setFiles([...files, ...filesToAdd])
    }
    handleFilesAsync()
  }

  const handleDrop = event => {
    event.preventDefault()
    if (files?.length + event.dataTransfer.files?.length > maxFiles) {
      return
    }
    for (let i = 0; i < event.dataTransfer.files?.length; i++) {
      if (
        !acceptFiles.includes(event.dataTransfer.files[i].name.split('.').pop())
      ) {
        setShowAlert('shipping:extension-error')
        return
      }
      if (event.dataTransfer.files[i].size > 5000000) {
        setShowAlert('shipping:size-error')
        return
      }
      if (event.dataTransfer.files[i].name.length >= 244) {
        setShowAlert('shipping:name-error')
        return
      }
    }
    handleCheckFiles([...event.dataTransfer.files])
  }

  const handleOnAdd = event => {
    event.preventDefault()
    if (files?.length + event.target.files?.length > maxFiles) {
      return
    }
    for (let i = 0; i < event.target.files?.length; i++) {
      if (!acceptFiles.includes(event.target.files[i].name.split('.').pop())) {
        setShowAlert('shipping:extension-error')
        return
      }
      if (event.target.files[i].size > 5000000) {
        setShowAlert('shipping:size-error')
        return
      }
      if (event.target.files[i].name.length >= 244) {
        setShowAlert('shipping:name-error')
        return
      }
    }
    handleCheckFiles([...event.target.files])
  }

  const handleOnDelete = id => {
    const newFiles = [...files]
    const index = newFiles.findIndex(element => element.id === id)

    newFiles.splice(index, 1)
    if (onDelete) onDelete(id)
    setFiles(newFiles)
  }

  const handleOnChange = async (id, value) => {
    const newFiles = [...files]
    const index = newFiles.findIndex(element => element.id === id)

    if (!newFiles[index]?.fileBodyPOST?.file_type) {
      newFiles[index].loading = true
      setFiles(newFiles)

      newFiles[index].type = value
      newFiles[index].fileBodyPOST.file_type = value

      await uploadFile(newFiles[index]?.fileBodyPOST, {
        onSuccess: uploadedData => {
          if (!isErrorFile) {
            newFiles[index].resource_provider = uploadedData?.resource_provider
            newFiles[index].resource_id = uploadedData?.resource_id
          }
          newFiles[index].loading = false
        },
        onError: error => {
          if (error) {
            newFiles[index].error = error
            setShowAlert('shipping:upload-error')
          }
          newFiles[index].loading = false
        },
      })
    } else {
      newFiles[index].loading = true
      setFiles(newFiles)

      newFiles[index].type = value
      newFiles[index].fileBodyPOST.file_type = value

      await uploadFile(newFiles[index]?.fileBodyPOST, {
        onSuccess: uploadedData => {
          if (!isErrorFile) {
            newFiles[index].resource_provider = uploadedData?.resource_provider
            newFiles[index].resource_id = uploadedData?.resource_id
          }
          newFiles[index].loading = false
        },
        onError: error => {
          if (error) {
            newFiles[index].error = error
            setShowAlert('shipping:upload-error')
          }
          newFiles[index].loading = false
        },
      })
    }

    newFiles[index].resource_provider = data?.resource_provider
    newFiles[index].resource_id = data?.resource_id
    if (onChange) onChange(newFiles[index])
    setFiles(newFiles)
  }

  const formatName = fileName => {
    const maxLength = 40
    if (fileName.length <= maxLength) {
      return fileName
    } else {
      const extensionIndex = fileName.lastIndexOf('.')
      const nameWithoutExtension = fileName.substring(0, extensionIndex)
      const extension = fileName.substring(extensionIndex)
      const truncatedName =
        nameWithoutExtension.substring(0, maxLength - extension.length) + '...'
      return truncatedName + extension
    }
  }
  
  return (
    <>
      <Box
        className={classes.dropzone}
        style={
          !files?.length
            ? { justifyContent: 'center', alignItems: 'center' }
            : null
        }
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {files?.length ? (
          <>
            {files.map((e, index) => (
              <Box key={e?.id} className={classes.rowFile}>
                <Box className={classes.fileBox}>
                  {e?.loading ? (
                    <CircularProgress
                      size={30}
                      sx={{
                        height: 'auto !important',
                        marginInlineEnd: 2,
                      }}
                    />
                  ) : e?.error ? (
                    <IconError className={classes.errorIcon} />
                  ) : (
                    <IconSuccesful className={classes.successIcon} />
                  )}
                  <Box className={classes.textFile}>
                    {formatName(e?.file?.name)}
                  </Box>
                  <IconClose
                    className={classes.closeIcon}
                    onClick={() => {
                      handleOnDelete(e?.id)
                    }}
                  />
                </Box>
                <Box className={classes.selectTypeFile}>
                  <Select
                    name="fileType"
                    error={!e?.type}
                    value={e?.type ?? null}
                    className={
                      e?.type ? classes.select : classes.selectDisabled
                    }
                    onChange={value => {
                      handleOnChange(e?.id, value?.target?.value)
                    }}
                  >
                    {CUSTOM_FILE_TYPE
                      ? CUSTOM_FILE_TYPE.map(({ text, id }) => (
                          <MenuItem key={id} value={`${id}`}>
                            {t(text)}
                          </MenuItem>
                        ))
                      : null}
                    <MenuItem value={null} disabled>
                      {t('action:file-type')}
                    </MenuItem>
                  </Select>
                </Box>
              </Box>
            ))}
          </>
        ) : (
          <Box
            className={classes.uploaderBox}
            onClick={() => inputRef.current.click()}
          >
            <IconUpload className={classes.uploaderIcon} />
            <Typography className={classes.primaryText} variant="h2">
              {t('action:upload-files')}
            </Typography>
            <Typography className={classes.secondaryText}>
              {t('action:drag-files')}
            </Typography>
          </Box>
        )}

        <input
          type="file"
          multiple={maxFiles > 1}
          onChange={event => handleOnAdd(event)}
          hidden
          accept={acceptFiles}
          ref={inputRef}
        />
      </Box>
      <Box className={classes.rowUploader}>
        {files?.length && files?.length < maxFiles ? (
          <IconButton
            onClick={() => inputRef.current.click()}
            className={classes.uploadButton}
            sx={{ mt: '28px' }}
          >
            <Plus />
          </IconButton>
        ) : null}
        {showAlert ? (
          <Alert
            className={classes.errorMessage}
            severity="error"
            iconAlignment="top"
          >
            {t(showAlert)}
          </Alert>
        ) : (
          <Box className={classes.errorMessage} />
        )}
      </Box>
    </>
  )
}

FileUploader.propTypes = {
  height: PropTypes.number,
  centreCode: PropTypes.number,
  maxFiles: PropTypes.number,
  acceptFiles: PropTypes.string,
  initialFiles: PropTypes.arrayOf(PropTypes.files),
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onError: PropTypes.func,
  onChange: PropTypes.func,
}

export default FileUploader
